import type { AxiosInstance } from "axios";
import type { QueryClient } from "@tanstack/react-query";
import { z } from "zod";
import {
  UserEnrichmentRequestDetailViewSchema,
  UserEnrichmentRequestRowSchema,
  UserEnrichmentTriggerResultSchema,
  type UserEnrichmentTriggerResult,
} from "@shared/schemas/user-enrichment-request-admin.schemas";
import { EnrichedUserDataViewSchema } from "@shared/schemas/enriched-user-data-admin.schemas";
import { BaseApiHooksRepository } from "../../base-api-hooks.repository";
import {
  createUserEnrichmentAdminApi,
  type UserEnrichmentRequestsQuery,
} from "./user-enrichment-admin.api";

const REQUESTS_STALE_TIME_MS = 30_000;

// The list endpoint returns a paginated envelope; parsing it here (rather than
// in the api client) is the repository's DTO→domain boundary, and z.coerce.date
// turns wire ISO strings into the Date objects the components rely on.
const RequestsPageSchema = z.object({
  items: z.array(UserEnrichmentRequestRowSchema),
  total: z.number(),
  page: z.number(),
  limit: z.number(),
  totalPages: z.number(),
});

export interface TriggerUserEnrichmentVariables {
  readonly participantIdsPublic?: readonly string[];
  readonly userEnrichmentRequestIdsPublic?: readonly string[];
}

const QUERY_KEYS = {
  requests: (query: UserEnrichmentRequestsQuery) =>
    [
      "user-enrichment-admin",
      "requests",
      query.page ?? null,
      query.limit ?? null,
    ] as const,
  requestsRoot: () => ["user-enrichment-admin", "requests"] as const,
  request: (idPublic: string) =>
    ["user-enrichment-admin", "request", idPublic] as const,
  enrichedUserData: (idPublic: string) =>
    ["user-enrichment-admin", "enriched-user-data", idPublic] as const,
};

export class UserEnrichmentAdminHooksHttpRepositoryImpl extends BaseApiHooksRepository {
  private readonly api;

  constructor(queryClient: QueryClient, apiClient: AxiosInstance) {
    super(queryClient);
    this.api = createUserEnrichmentAdminApi(apiClient);
  }

  createHooks() {
    const { api } = this;
    return {
      useGetUserEnrichmentRequests: (query: UserEnrichmentRequestsQuery = {}) =>
        this.query({
          key: QUERY_KEYS.requests(query),
          fn: () => api.getRequests(query),
          parse: (dto) => RequestsPageSchema.parse(dto),
          staleTime: REQUESTS_STALE_TIME_MS,
        }),

      useGetUserEnrichmentRequest: (idPublic: string) =>
        this.query({
          key: QUERY_KEYS.request(idPublic),
          fn: () => api.getRequest(idPublic),
          parse: (dto) => UserEnrichmentRequestDetailViewSchema.parse(dto),
          enabled: idPublic !== "",
          staleTime: REQUESTS_STALE_TIME_MS,
        }),

      useGetEnrichedUserData: (idPublic: string) =>
        this.query({
          key: QUERY_KEYS.enrichedUserData(idPublic),
          fn: () => api.getEnrichedData(idPublic),
          parse: (dto) => EnrichedUserDataViewSchema.parse(dto),
          enabled: idPublic !== "",
          staleTime: REQUESTS_STALE_TIME_MS,
        }),

      useTriggerUserEnrichment: () =>
        this.mutation<
          readonly unknown[],
          readonly UserEnrichmentTriggerResult[],
          TriggerUserEnrichmentVariables
        >({
          fn: (variables) => this.api.trigger(variables),
          parse: (dtos) =>
            dtos.map((dto) => UserEnrichmentTriggerResultSchema.parse(dto)),
          onSuccess: () => {
            this.invalidateQueries(QUERY_KEYS.requestsRoot());
          },
        }),
    };
  }
}
